import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiShoppingCart } from 'react-icons/fi';
import { useCart } from '../context/CartContext';

// LOWES SHEDS logo.
const Wordmark = ({ className = 'h-10' }) => (
  <img
    src="/logo.png"
    alt="Lowes Sheds"
    className={`${className} w-auto`}
  />
);

const HeaderShed = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const { cartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Sheds', href: '/sheds' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 w-full transition-all duration-300 bg-primary-900 ${
        isScrolled ? 'shadow-md' : ''
      } ${isVisible ? 'translate-y-0' : 'lg:-translate-y-full'}`}
      style={{ zIndex: 9996 }}
    >
      <div className="container mx-auto px-4">
        {/* Desktop */}
        <div className="hidden lg:flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <Wordmark />
          </Link>

          <nav className="flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm transition-colors ${
                  isActive(item.href) ? 'text-white font-semibold' : 'text-primary-100 hover:text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <Link
              to="/inventory"
              className="bg-white text-primary-900 px-5 py-2 text-sm font-semibold rounded-sm hover:bg-primary-50 transition-colors"
            >
              Build Your Shed
            </Link>
            <Link to="/cart" className="relative text-primary-100 hover:text-white transition-colors">
              <FiShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 bg-white text-primary-900 text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Mobile */}
        <div className="flex lg:hidden justify-between items-center h-14">
          <Link to="/" className="flex items-center">
            <Wordmark className="h-9" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/cart" className="relative text-primary-100">
              <FiShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 bg-white text-primary-900 text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              className="p-1 text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-200 lg:hidden ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ zIndex: 9998 }}
        onClick={() => setIsMenuOpen(false)}
      />

      {isMenuOpen && (
        <aside
          className="fixed top-0 right-0 bg-white lg:hidden flex flex-col"
          style={{ height: '100vh', width: '80%', maxWidth: '320px', zIndex: 9999 }}
        >
          <div className="flex items-center justify-between px-6 py-4 bg-primary-900">
            <Wordmark />
            <button onClick={() => setIsMenuOpen(false)} className="text-white">
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-6 px-6">
            <nav className="space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block py-3 border-b border-gray-100 text-sm transition-colors ${
                    isActive(item.href) ? 'text-primary-900 font-semibold' : 'text-gray-600 hover:text-primary-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            <div className="mt-8 space-y-3">
              <Link
                to="/inventory"
                className="block bg-primary-900 text-white px-5 py-3 text-sm font-semibold text-center rounded-sm hover:bg-primary-800 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Build Your Shed
              </Link>
            </div>
          </div>
        </aside>
      )}
    </header>
  );
};

export default HeaderShed;
